import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import './items.css';
import { useZohoOrgId, zohoItemUrl } from '../../../utils/zohoOrg';
import EditItem from './EditItem';
import AddItem from './AddItem';

const FILTER_OPTIONS = [
    { value: 'all', label: 'All Items' },
    { value: 'addon', label: 'Add-ons' },
    { value: 'laundry', label: 'Laundry' },
    { value: 'custom', label: 'Custom Price' },
    { value: 'standard', label: 'Standard' },
];

function Items() {
    const zohoOrgId = useZohoOrgId();
    const [items, setItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    const fetchItems = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/items');
            if (response.status === 200) {
                setItems(response.data.items.sort((a, b) => a.id - b.id));
            }
        } catch {
            setError('Failed to load items.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchItems(); }, []);

    const filteredItems = useMemo(() => {
        let list = items;

        // Category filter
        if (filter === 'addon') list = list.filter(i => i.is_addon);
        else if (filter === 'laundry') list = list.filter(i => i.is_laundry);
        else if (filter === 'custom') list = list.filter(i => i.property_id);
        else if (filter === 'standard') list = list.filter(i => !i.is_addon && !i.is_laundry && !i.property_id);

        // Search
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(i =>
                i.name.toLowerCase().includes(term) ||
                String(i.id).includes(term) ||
                (i.zoho_item_id || '').toLowerCase().includes(term) ||
                String(i.property_id || '').includes(term)
            );
        }

        return list;
    }, [items, filter, searchTerm]);

    const stats = useMemo(() => ({
        total: items.length,
        addons: items.filter(i => i.is_addon).length,
        laundry: items.filter(i => i.is_laundry).length,
        custom: items.filter(i => i.property_id).length,
    }), [items]);

    const money = (n) => {
        if (n === null || n === undefined || isNaN(n)) return '—';
        return `AED ${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const handleSave = (updatedItem) => {
        setItems(prev => prev.map(i => i.id === updatedItem.id ? updatedItem : i));
    };

    return (
        <div className="items-page">
            <div className="items-page__header">
                <div>
                    <h1 className="items-page__title">Items</h1>
                    <p className="items-page__subtitle">{stats.total} items · {stats.addons} add-ons · {stats.laundry} laundry · {stats.custom} custom price</p>
                </div>
                <button className="items-page__addBtn" onClick={() => setIsAddModalOpen(true)}>
                    + Add Item
                </button>
            </div>

            {/* Filters row */}
            <div className="items-page__toolbar">
                <div className="items-page__filters">
                    {FILTER_OPTIONS.map(f => (
                        <button
                            key={f.value}
                            className={`items-page__filterBtn ${filter === f.value ? 'is-active' : ''}`}
                            onClick={() => setFilter(f.value)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <input
                    type="text"
                    className="items-page__search"
                    placeholder="Search by name, ID, Zoho ID..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
            </div>

            {loading && (
                <div className="app-loading">
                    <div className="app-spinner" />
                    <div className="app-spinner-text">Loading Items...</div>
                </div>
            )}
            {error && <div className="items-page__error">{error}</div>}

            {!loading && !error && (
                <div className="items-page__tableWrap">
                    <table className="items-page__table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Name</th>
                                <th>Price</th>
                                <th>Type</th>
                                <th>Zoho ID</th>
                                <th>Property</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredItems.length === 0 ? (
                                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#9ca3af', padding: 30 }}>No items match your filters.</td></tr>
                            ) : filteredItems.map(item => (
                                <tr key={item.id}>
                                    <td className="items-page__id">#{String(item.id).padStart(5, '0')}</td>
                                    <td className="items-page__name">{item.name}</td>
                                    <td className="items-page__price">{money(item.default_price)}</td>
                                    <td>
                                        <div className="items-page__tags">
                                            {item.is_addon && <span className="items-page__tag items-page__tag--green">Add-on</span>}
                                            {item.is_laundry && <span className="items-page__tag items-page__tag--blue">Laundry</span>}
                                            {item.property_id && <span className="items-page__tag items-page__tag--orange">Custom</span>}
                                            {!item.is_addon && !item.is_laundry && !item.property_id && <span className="items-page__tag">Standard</span>}
                                        </div>
                                    </td>
                                    <td className="items-page__zoho">
                                        {item.zoho_item_id && zohoOrgId ? (
                                            <a
                                                href={zohoItemUrl(zohoOrgId, item.zoho_item_id)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="items-page__zohoLink"
                                            >
                                                {item.zoho_item_id}
                                            </a>
                                        ) : '—'}
                                    </td>
                                    <td>{item.property_id ? `#${item.property_id}` : '—'}</td>
                                    <td>
                                        <button
                                            className="items-page__editBtn"
                                            onClick={() => { setSelectedItem(item); setIsEditModalOpen(true); }}
                                        >
                                            Edit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!loading && !error && (
                <div className="items-page__count">{filteredItems.length} of {items.length} items shown</div>
            )}

            {isEditModalOpen && (
                <EditItem
                    item={selectedItem}
                    isOpen={isEditModalOpen}
                    onClose={() => { setSelectedItem(null); setIsEditModalOpen(false); }}
                    onSave={handleSave}
                />
            )}

            {isAddModalOpen && (
                <AddItem
                    isOpen={isAddModalOpen}
                    onClose={() => setIsAddModalOpen(false)}
                    onSave={(newItem) => setItems(prev => [...prev, newItem])}
                />
            )}
        </div>
    );
}

export default Items;
